// publish-85 round-9 · Publish Health page (owner-only).
//
// Three panels:
//   1. Infrastructure — scheduler heartbeat, posting hold, incidents,
//      per-connection health (GET /publish/health).
//   2. Orphans — receipts stuck in publishing / pending_platform, interrupted
//      live-runs and failed rows that already carry a provider id
//      (GET /publish/health/orphans). Each row offers:
//        * "Set provider ID" — inline form → POST /publish-receipts/{id}/set-provider-id
//          (item B4: repair an orphan so delete-from-platform can address it)
//        * "Delete from platform" when an id is known.
//   3. Reconcile — pick a Facebook / Instagram connection, list platform posts
//      from the last N hours that Brand OS has no receipt for
//      (GET /publish/reconcile/{connection_id}) and Adopt them
//      (POST /publish/reconcile/{connection_id}/adopt) — item C.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { channelDisplay, fmtDateTimeTz, useOrgTimezone } from "../lib/tz";

const STATUS_TONE = {
  publishing:         "warn",
  pending_platform:   "warn",
  live_run_pending:   "warn",
  live_run_errored:   "danger",
  live_run_cancelled: "danger",
  failed:             "danger",
  published:          "success",
  adopted_untracked:  "success",
};

const TONE_COLOR = {
  success: "var(--bo-success, #0a7a3b)",
  warn:    "var(--bo-warn, #a05a00)",
  danger:  "var(--bo-danger, #b1261a)",
  muted:   "var(--bo-muted)",
};

function Pill({ tone = "muted", children, testId }) {
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-block", padding: "2px 8px", borderRadius: 999,
        fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
        border: `1px solid ${TONE_COLOR[tone] || TONE_COLOR.muted}`,
        color: TONE_COLOR[tone] || TONE_COLOR.muted,
      }}
    >
      {children}
    </span>
  );
}

function fmtAge(s) {
  if (s === null || s === undefined) return "—";
  if (s < 90) return `${s}s`;
  if (s < 5400) return `${Math.round(s / 60)}m`;
  if (s < 172800) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

const td = { padding: "8px 10px", verticalAlign: "top", fontSize: 13 };
const th = { ...td, fontSize: 11, textTransform: "uppercase",
             letterSpacing: 0.4, color: "var(--bo-muted)", textAlign: "left" };

export default function PublishHealth() {
  const tz = useOrgTimezone();
  const [health, setHealth] = useState(null);
  const [healthErr, setHealthErr] = useState(null);
  const [orphans, setOrphans] = useState(null);
  const [orphansErr, setOrphansErr] = useState(null);
  const [loadingOrphans, setLoadingOrphans] = useState(false);

  const loadHealth = useCallback(async () => {
    try {
      const r = await api.get("/publish/health");
      setHealth(r.data || null);
      setHealthErr(null);
    } catch (e) {
      setHealthErr(formatApiError(e) || e.message);
    }
  }, []);

  const loadOrphans = useCallback(async () => {
    setLoadingOrphans(true);
    try {
      const r = await api.get("/publish/health/orphans?limit=100");
      setOrphans(r.data || { orphans: [], count: 0 });
      setOrphansErr(null);
    } catch (e) {
      setOrphansErr(formatApiError(e) || e.message);
    } finally {
      setLoadingOrphans(false);
    }
  }, []);

  useEffect(() => { loadHealth(); loadOrphans(); }, [loadHealth, loadOrphans]);

  const metaConnections = useMemo(
    () => (health?.connections || []).filter(
      (c) => c.channel === "facebook" || c.channel === "instagram"),
    [health],
  );

  return (
    <Shell title="Publish Health">
      <div className="bo-page" data-testid="publish-health-page" style={{ padding: 24 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start",
                      marginBottom: 20, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 320 }}>
            <div className="bo-eyebrow">Publish</div>
            <h1 className="bo-serif" style={{ fontSize: 26, margin: "4px 0" }}>Health</h1>
            <p style={{ color: "var(--bo-muted)", maxWidth: 760, lineHeight: 1.55 }}>
              Everything that can leave a post on a platform without a matching
              Brand OS receipt lives here: receipts stuck mid-publish, interrupted
              live-runs, and platform posts we never recorded. Repair an orphan by
              setting its provider id, or adopt an untracked post so it can be
              deleted from Brand OS.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Link to="/brand-os/publish/queue" className="bo-btn bo-btn-ghost"
                  style={{ textDecoration: "none" }} data-testid="health-back-queue">← Queue</Link>
            <Link to="/brand-os/admin/channel-connections" className="bo-btn bo-btn-ghost"
                  style={{ textDecoration: "none" }} data-testid="health-open-connections">Connections →</Link>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 1 · Infrastructure                                                 */}
        {/* ---------------------------------------------------------------- */}
        <div className="bo-card" style={{ padding: 18, marginBottom: 16 }}
             data-testid="health-infra-card">
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div className="bo-eyebrow">Infrastructure</div>
            <button className="bo-btn bo-btn-ghost" onClick={loadHealth}
                    data-testid="health-refresh-infra" style={{ fontSize: 12 }}>
              Refresh
            </button>
          </div>
          {healthErr && (
            <div style={{ color: "var(--bo-danger)", marginTop: 8 }}
                 data-testid="health-infra-error">{String(healthErr)}</div>
          )}
          {health && (
            <div style={{ display: "grid", gap: 12, marginTop: 12,
                          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
              <Stat label="Scheduler" testId="health-stat-scheduler"
                    tone={health.scheduler_stalled ? "danger" : "success"}
                    value={health.scheduler_stalled ? "STALLED" : "HEALTHY"}
                    sub={`heartbeat ${health.scheduler_heartbeat_age_s ?? "n/a"}s ago`} />
              <Stat label="Posting hold" testId="health-stat-hold"
                    tone={health.posting_hold ? "warn" : "success"}
                    value={health.posting_hold ? "ON" : "OFF"}
                    sub={health.posting_hold ? "only acceptance tests publish" : "normal publishing"} />
              <Stat label="Open incidents" testId="health-stat-incidents"
                    tone={health.open_incident_count > 0 ? "danger" : "success"}
                    value={String(health.open_incident_count ?? 0)} />
              <Stat label="Orphan candidates" testId="health-stat-orphans"
                    tone={(orphans?.count || 0) > 0 ? "warn" : "success"}
                    value={String(orphans?.count ?? "…")}
                    sub={orphans ? `${orphans.stuck_publishing_count} stuck · ${orphans.missing_provider_id_count} without id` : ""} />
            </div>
          )}
          {health && (health.connections || []).length > 0 && (
            <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}
                 data-testid="health-connections-strip">
              {health.connections.map((c) => (
                <span key={c.id}
                      data-testid={`health-connection-${c.id}`}
                      title={c.health_reason || ""}
                      style={{ fontSize: 12, padding: "4px 10px", borderRadius: 8,
                               border: "1px solid var(--bo-line)" }}>
                  {c.display_name || channelDisplay(c.channel)}{" "}
                  <Pill tone={c.health === "HEALTHY" ? "success"
                              : c.health === "UNKNOWN" || !c.health ? "muted" : "danger"}>
                    {c.health || "UNKNOWN"}
                  </Pill>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 2 · Orphans                                                        */}
        {/* ---------------------------------------------------------------- */}
        <div className="bo-card" style={{ padding: 18, marginBottom: 16 }}
             data-testid="health-orphans-card">
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div className="bo-eyebrow">Orphans &amp; stuck receipts</div>
              <div style={{ color: "var(--bo-muted)", fontSize: 13, marginTop: 4,
                            maxWidth: 720, lineHeight: 1.5 }}>
                Receipts in <code>publishing</code> for more than 2 minutes with a
                provider id are picked up by the background poller automatically.
                Rows <em>without</em> an id need you: paste the platform's
                video / media / pin id and we'll mark the receipt published so
                Delete-from-platform works.
              </div>
            </div>
            <button className="bo-btn bo-btn-ghost" onClick={loadOrphans}
                    disabled={loadingOrphans} data-testid="health-refresh-orphans"
                    style={{ fontSize: 12 }}>
              {loadingOrphans ? "Loading…" : "Refresh"}
            </button>
          </div>

          {orphansErr && (
            <div style={{ color: "var(--bo-danger)", marginTop: 8 }}
                 data-testid="health-orphans-error">{String(orphansErr)}</div>
          )}
          {orphans && orphans.count === 0 && (
            <div style={{ marginTop: 14, padding: 14, color: "var(--bo-muted)",
                          border: "1px dashed var(--bo-line)", borderRadius: 8 }}
                 data-testid="health-orphans-empty">
              No stuck or orphaned receipts. Every publish has a terminal receipt.
            </div>
          )}
          {orphans && orphans.count > 0 && (
            <div style={{ overflowX: "auto", marginTop: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}
                     data-testid="health-orphans-table">
                <thead>
                  <tr>
                    <th style={th}>Channel</th>
                    <th style={th}>Status</th>
                    <th style={th}>Age</th>
                    <th style={th}>Provider id</th>
                    <th style={th}>Failure</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.orphans.map((r) => (
                    <OrphanRow key={r.id} row={r} tz={tz} onChanged={loadOrphans} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* 3 · Reconcile                                                      */}
        {/* ---------------------------------------------------------------- */}
        <ReconcilePanel connections={metaConnections} tz={tz} onAdopted={loadOrphans} />
      </div>
    </Shell>
  );
}


function Stat({ label, value, sub, tone = "muted", testId }) {
  return (
    <div data-testid={testId}
         style={{ padding: 12, borderRadius: 10, border: "1px solid var(--bo-line)" }}>
      <div className="bo-eyebrow" style={{ fontSize: 10 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: TONE_COLOR[tone], marginTop: 2 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--bo-muted)" }}>{sub}</div>}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Orphan row · inline "Set provider ID" + "Delete from platform"
// ---------------------------------------------------------------------------

const PROVIDER_ID_HINT = {
  facebook:  "Facebook video / post id (digits, e.g. 1180447638495415_12345)",
  instagram: "Instagram media id (digits)",
  youtube:   "YouTube video id (11 chars, e.g. dQw4w9WgXcQ)",
  pinterest: "Pinterest pin id (digits)",
  tiktok:    "TikTok publish_id (v_pub_…) or video id",
};

function OrphanRow({ row: r, tz, onChanged }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(r.external_artifact_id || "");
  const [url, setUrl] = useState(r.external_artifact_url || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [deleteState, setDeleteState] = useState(null);
  const base = String(r.channel || "").split(":")[0];
  const tone = STATUS_TONE[r.execution_status] || "muted";
  const rid = r.id;

  async function save() {
    if (!val.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const resp = await api.post(`/publish-receipts/${rid}/set-provider-id`, {
        external_artifact_id: val.trim(),
        external_artifact_url: url.trim() || null,
        mark_published: true,
        note: "set from Publish Health page",
      });
      setMsg({ tone: "success",
               text: `Saved · receipt now ${resp.data?.execution_status || "published"}` });
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      setMsg({ tone: "danger", text: formatApiError(e) || e.message });
    } finally {
      setBusy(false);
    }
  }

  async function deleteNow() {
    if (!window.confirm("Delete this post from the platform?")) return;
    setBusy(true); setDeleteState(null);
    try {
      const resp = await api.post(`/publish-receipts/${rid}/delete-from-platform`);
      setDeleteState(resp.data || { status: "unknown" });
      onChanged && onChanged();
    } catch (e) {
      setDeleteState({ status: "failed", reason: formatApiError(e) || e.message });
    } finally {
      setBusy(false);
    }
  }

  const ids = Object.entries(r.provider_ids || {});
  return (
    <>
      <tr data-testid={`orphan-row-${rid}`} style={{ borderTop: "1px solid var(--bo-line)" }}>
        <td style={td}>
          <div>{channelDisplay(base)}</div>
          <div style={{ fontFamily: "var(--bo-mono)", fontSize: 11, color: "var(--bo-muted)" }}>
            {r.channel}
          </div>
          {r.connection?.display_name && (
            <div style={{ fontSize: 11, color: "var(--bo-muted)" }}>{r.connection.display_name}</div>
          )}
        </td>
        <td style={td}>
          <Pill tone={tone} testId={`orphan-status-${rid}`}>{r.execution_status}</Pill>
          {r.stuck && (
            <div style={{ fontSize: 11, color: TONE_COLOR.warn, marginTop: 4 }}
                 data-testid={`orphan-stuck-${rid}`}>
              stuck &gt;2 min{r.has_provider_id ? " · poller will adopt" : " · needs id"}
            </div>
          )}
          {r.in_flight && (
            <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 2 }}>task still running</div>
          )}
          {r.is_acceptance_test && (
            <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 2 }}>acceptance test</div>
          )}
        </td>
        <td style={td} title={r.updated_at ? fmtDateTimeTz(r.updated_at, tz) : ""}>
          <span data-testid={`orphan-age-${rid}`}>{fmtAge(r.age_s)}</span>
        </td>
        <td style={{ ...td, fontFamily: "var(--bo-mono)", fontSize: 12 }}>
          {r.external_artifact_id ? (
            <div data-testid={`orphan-artifact-${rid}`}>
              {r.external_artifact_url
                ? <a href={r.external_artifact_url} target="_blank" rel="noreferrer">{r.external_artifact_id}</a>
                : r.external_artifact_id}
            </div>
          ) : (
            <span style={{ color: TONE_COLOR.danger }} data-testid={`orphan-no-id-${rid}`}>no id</span>
          )}
          {ids.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 2 }}>
              {ids.map(([k, v]) => `${k}=${v}`).join(" · ")}
            </div>
          )}
        </td>
        <td style={{ ...td, fontSize: 12, maxWidth: 260 }}>
          {r.failure_code ? (
            <div title={r.failure_message || ""}>
              <div style={{ fontFamily: "var(--bo-mono)", fontSize: 11 }}>{r.failure_code}</div>
              {r.failure_message && (
                <div style={{ color: "var(--bo-muted)" }}>
                  {r.failure_message.length > 110 ? r.failure_message.slice(0, 110) + "…" : r.failure_message}
                </div>
              )}
            </div>
          ) : <span style={{ color: "var(--bo-muted)" }}>—</span>}
          {r.platform_delete_status && (
            <div style={{ fontSize: 11, marginTop: 2, color: "var(--bo-muted)" }}>
              delete: {r.platform_delete_status}
            </div>
          )}
        </td>
        <td style={td}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
            <button type="button" className="bo-btn bo-btn-ghost"
                    data-testid={`orphan-set-id-btn-${rid}`}
                    onClick={() => setOpen((o) => !o)} disabled={busy}
                    style={{ padding: "4px 10px", fontSize: 11 }}>
              {open ? "Cancel" : r.external_artifact_id ? "Edit provider ID" : "Set provider ID"}
            </button>
            {r.has_provider_id && r.delete_supported && !deleteState && (
              <button type="button" className="bo-btn bo-btn-danger"
                      data-testid={`orphan-delete-btn-${rid}`}
                      onClick={deleteNow} disabled={busy}
                      style={{ padding: "4px 10px", fontSize: 11 }}>
                Delete from platform
              </button>
            )}
            <Link to={`/brand-os/publish/receipts/${rid}`} style={{ fontSize: 11 }}
                  data-testid={`orphan-receipt-link-${rid}`}>
              receipt {rid.slice(0, 8)}
            </Link>
            {msg && (
              <div style={{ fontSize: 11, color: TONE_COLOR[msg.tone] }}
                   data-testid={`orphan-set-id-msg-${rid}`}>{msg.text}</div>
            )}
            {deleteState && (
              <div style={{ fontSize: 11,
                            color: deleteState.status === "deleted" || deleteState.status === "already_deleted"
                                   ? TONE_COLOR.success : TONE_COLOR.danger }}
                   data-testid={`orphan-delete-state-${rid}`}>
                Delete: {deleteState.status || "unknown"}
                {deleteState.reason && <div style={{ color: "var(--bo-muted)" }}>{String(deleteState.reason)}</div>}
                {deleteState.manual_help && <div style={{ color: "var(--bo-muted)" }}>{deleteState.manual_help}</div>}
              </div>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr data-testid={`orphan-set-id-form-${rid}`}>
          <td colSpan={6} style={{ ...td, background: "var(--bo-surface-2, rgba(0,0,0,.02))" }}>
            <div style={{ display: "grid", gap: 8,
                          gridTemplateColumns: "minmax(220px, 1fr) minmax(220px, 1.4fr) auto",
                          alignItems: "end" }}>
              <div style={{ display: "grid", gap: 4 }}>
                <label className="bo-label" htmlFor={`pid-${rid}`}>Provider id *</label>
                <input id={`pid-${rid}`} className="bo-input" value={val}
                       onChange={(e) => setVal(e.target.value)}
                       placeholder={PROVIDER_ID_HINT[base] || "platform artifact id"}
                       data-testid={`orphan-set-id-input-${rid}`} />
              </div>
              <div style={{ display: "grid", gap: 4 }}>
                <label className="bo-label" htmlFor={`purl-${rid}`}>Permalink (optional)</label>
                <input id={`purl-${rid}`} className="bo-input" value={url}
                       onChange={(e) => setUrl(e.target.value)}
                       placeholder="https://…"
                       data-testid={`orphan-set-url-input-${rid}`} />
              </div>
              <button type="button" className="bo-btn bo-btn-primary"
                      onClick={save} disabled={busy || !val.trim()}
                      data-testid={`orphan-set-id-save-${rid}`}>
                {busy ? "Saving…" : "Save & mark published"}
              </button>
            </div>
            <div style={{ fontSize: 11, color: "var(--bo-muted)", marginTop: 6 }}>
              {PROVIDER_ID_HINT[base]} — audited as <code>publish_receipt.provider_id_set_manually</code>.
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// Reconcile panel · list untracked Page posts / reels → Adopt
// ---------------------------------------------------------------------------

function ReconcilePanel({ connections, tz, onAdopted }) {
  const [connId, setConnId] = useState("");
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!connId && connections.length > 0) setConnId(connections[0].id);
  }, [connections, connId]);

  async function scan() {
    if (!connId) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.get(`/publish/reconcile/${connId}?since_hours=${hours}`);
      setResult(r.data);
    } catch (e) {
      setError(formatApiError(e) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bo-card" style={{ padding: 18 }} data-testid="health-reconcile-card">
      <div className="bo-eyebrow">Reconcile with the platform</div>
      <div style={{ color: "var(--bo-muted)", fontSize: 13, marginTop: 4, maxWidth: 720, lineHeight: 1.5 }}>
        Lists Facebook Page posts and reels (or Instagram media) created in the
        last {hours}h that Brand OS has no receipt for. Adopt one to create a
        receipt you can delete from here.
      </div>

      {connections.length === 0 ? (
        <div style={{ marginTop: 14, padding: 14, color: "var(--bo-muted)",
                      border: "1px dashed var(--bo-line)", borderRadius: 8 }}
             data-testid="reconcile-no-connections">
          No active Facebook / Instagram connection. Connect one in{" "}
          <Link to="/brand-os/admin/channel-connections">Channel Connections</Link>.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap", marginTop: 12 }}>
          <div style={{ display: "grid", gap: 4, minWidth: 260 }}>
            <label className="bo-label" htmlFor="reconcile-conn">Connection</label>
            <select id="reconcile-conn" className="bo-input" value={connId}
                    onChange={(e) => setConnId(e.target.value)}
                    data-testid="reconcile-connection-select">
              {connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name || channelDisplay(c.channel)}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "grid", gap: 4, width: 120 }}>
            <label className="bo-label" htmlFor="reconcile-hours">Look back (h)</label>
            <input id="reconcile-hours" className="bo-input" type="number" min={1} max={168}
                   value={hours} onChange={(e) => setHours(Math.max(1, Math.min(168, Number(e.target.value) || 24)))}
                   data-testid="reconcile-hours-input" />
          </div>
          <button type="button" className="bo-btn bo-btn-primary" onClick={scan}
                  disabled={busy || !connId} data-testid="reconcile-scan-btn">
            {busy ? "Scanning…" : "Scan platform"}
          </button>
        </div>
      )}

      {error && (
        <div style={{ color: "var(--bo-danger)", marginTop: 10 }} data-testid="reconcile-error">
          {String(error)}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 14 }} data-testid="reconcile-result">
          <div style={{ fontSize: 12, color: "var(--bo-muted)", marginBottom: 8 }}
               data-testid="reconcile-summary">
            Scanned {result.scanned_rows} platform rows · {result.known_rows} already
            tracked · <strong style={{ color: result.untracked_count > 0 ? TONE_COLOR.warn : TONE_COLOR.success }}>
              {result.untracked_count} untracked
            </strong>
          </div>
          {result.untracked_count === 0 ? (
            <div style={{ padding: 12, color: "var(--bo-muted)",
                          border: "1px dashed var(--bo-line)", borderRadius: 8 }}
                 data-testid="reconcile-empty">
              Every platform post from the window has a Brand OS receipt.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}
                     data-testid="reconcile-table">
                <thead>
                  <tr>
                    <th style={th}>Kind</th>
                    <th style={th}>External id</th>
                    <th style={th}>Created</th>
                    <th style={th}>Message</th>
                    <th style={th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {result.untracked.map((u) => (
                    <UntrackedRow key={u.external_id} row={u} connId={connId} tz={tz}
                                  onAdopted={onAdopted} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function UntrackedRow({ row: u, connId, tz, onAdopted }) {
  const [busy, setBusy] = useState(false);
  const [adopted, setAdopted] = useState(u.adopted_receipt_id ? { receipt_id: u.adopted_receipt_id } : null);
  const [err, setErr] = useState(null);
  const [deleteState, setDeleteState] = useState(null);

  async function adopt() {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/publish/reconcile/${connId}/adopt`, {
        external_id: u.external_id, kind: u.kind || null, permalink: u.permalink || null,
      });
      setAdopted(r.data);
      onAdopted && onAdopted();
    } catch (e) {
      setErr(formatApiError(e) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteNow() {
    const rid = adopted?.receipt_id;
    if (!rid) return;
    if (!window.confirm("Delete this adopted post from the platform?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/publish-receipts/${rid}/delete-from-platform`);
      setDeleteState(r.data || { status: "unknown" });
    } catch (e) {
      setDeleteState({ status: "failed", reason: formatApiError(e) || e.message });
    } finally {
      setBusy(false);
    }
  }

  const eid = String(u.external_id);
  return (
    <tr data-testid={`reconcile-row-${eid}`} style={{ borderTop: "1px solid var(--bo-line)" }}>
      <td style={{ ...td, fontFamily: "var(--bo-mono)", fontSize: 12 }}>{u.kind || "—"}</td>
      <td style={{ ...td, fontFamily: "var(--bo-mono)", fontSize: 12 }}>
        {u.permalink
          ? <a href={u.permalink} target="_blank" rel="noreferrer" data-testid={`reconcile-permalink-${eid}`}>{eid}</a>
          : eid}
      </td>
      <td style={td}>{u.created_time ? fmtDateTimeTz(u.created_time, tz) : "—"}</td>
      <td style={{ ...td, fontSize: 12, maxWidth: 320, color: "var(--bo-muted)" }}>
        {u.message ? (u.message.length > 120 ? u.message.slice(0, 120) + "…" : u.message) : "—"}
      </td>
      <td style={td}>
        {!adopted ? (
          <button type="button" className="bo-btn bo-btn-primary" onClick={adopt}
                  disabled={busy} data-testid={`reconcile-adopt-btn-${eid}`}
                  style={{ padding: "4px 10px", fontSize: 11 }}>
            {busy ? "Adopting…" : "Adopt"}
          </button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
            <Pill tone="success" testId={`reconcile-adopted-${eid}`}>
              adopted{adopted.already_tracked ? " (already)" : ""}
            </Pill>
            <Link to={`/brand-os/publish/receipts/${adopted.receipt_id}`} style={{ fontSize: 11 }}
                  data-testid={`reconcile-receipt-link-${eid}`}>
              receipt {String(adopted.receipt_id).slice(0, 8)}
            </Link>
            {(adopted.delete_supported ?? true) && !deleteState && (
              <button type="button" className="bo-btn bo-btn-danger" onClick={deleteNow}
                      disabled={busy} data-testid={`reconcile-delete-btn-${eid}`}
                      style={{ padding: "4px 10px", fontSize: 11 }}>
                Delete from platform
              </button>
            )}
            {deleteState && (
              <div style={{ fontSize: 11,
                            color: deleteState.status === "deleted" ? TONE_COLOR.success : TONE_COLOR.danger }}
                   data-testid={`reconcile-delete-state-${eid}`}>
                Delete: {deleteState.status || "unknown"}
                {deleteState.reason && <div style={{ color: "var(--bo-muted)" }}>{String(deleteState.reason)}</div>}
              </div>
            )}
          </div>
        )}
        {err && (
          <div style={{ fontSize: 11, color: TONE_COLOR.danger, marginTop: 4 }}
               data-testid={`reconcile-adopt-error-${eid}`}>{String(err)}</div>
        )}
      </td>
    </tr>
  );
}
